import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { MediaRegistry } from '../registries/MediaRegistry';
import type { ToolExecutionContext } from '../types';

import { newSceneGroupBaseSceneNote } from './base-scene-note';
import { ConversationTool } from './ConversationTool';
import { computeSceneGroupShortIds, resolveInsertSlotAfter } from './scene-group-addressing';
import { findWordSequenceIndices } from './transcript-word-match';

import type { SlideBackgroundModel } from 'js/src/client';
import { applyPlaybackRegionsToSceneGroupOperation } from 'js/src/data/clip-creator/clip-creator-operations';
import { checkpointVideo } from 'js/src/data/video-data';
import { wordSpanToClipRegion } from 'js/src/data/transcript/word-timing';
import { ImmutableFromJS, type ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import {
    applyTalkingHeadMediaOperation,
    createNewVoicedrivenSceneGroupOperation,
    deleteSceneGroupOperation,
    saveRawLuminary,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const addTalkingHeadSceneGroupSchema = z.object({
    after_scene_group_id: z
        .string()
        .optional()
        .describe(
            'The stable scene_group_id of the scene group the new talking-head scene group should be inserted immediately after. Omit to insert it at the very start of the video.',
        ),
    media_id: z
        .string()
        .describe(
            `The ID of the uploaded video (with spoken audio) to use as the talking head. Must be a video media ID from ${TOOL_NAMES.listAvailableMedia} or ${TOOL_NAMES.searchMedia}.`,
        ),
    words: z
        .array(z.string())
        .optional()
        .describe(
            'Include only part of the recording: the exact transcript words of the segment to keep, in order (e.g. from a clip_select answer). The segment spans those words, so it starts and ends on a word boundary. Omit to include the whole recording.',
        ),
});
export type AddTalkingHeadSceneGroupArgs = z.infer<typeof addTalkingHeadSceneGroupSchema>;

export class AddTalkingHeadSceneGroupTool extends ConversationTool<
    typeof addTalkingHeadSceneGroupSchema
> {
    protected override readonly schema = addTalkingHeadSceneGroupSchema;
    protected override readonly toolName = TOOL_NAMES.addTalkingHeadSceneGroup;
    protected override readonly toolDescription = `Add a new talking-head scene group to the video at the specified index, driven by an uploaded video of a person speaking on camera. The video becomes the on-camera talking head and its speech is transcribed into a synced transcript. The media_id must reference an uploaded VIDEO that contains audio of someone speaking — find it with ${TOOL_NAMES.listAvailableMedia} or ${TOOL_NAMES.searchMedia}. To narrate with AI-generated voiceover from text instead, use ${TOOL_NAMES.addVoiceoverSceneGroup}.`;

    public override getToolDisplayName(): string {
        return 'Adding talking head scene group';
    }

    public get statusIcon(): string {
        return 'account_circle';
    }

    public async execute(args: AddTalkingHeadSceneGroupArgs, context: ToolExecutionContext) {
        const { after_scene_group_id, media_id, words } = args;
        const { parentActionGroup } = context;

        if (words && words.length === 0) {
            return `Error: "words" was empty. Pass the transcript words of the segment to keep, or omit it to include the whole recording.`;
        }

        // Resolve the insert slot up front (fail-closed on an unknown anchor id).
        const slot = resolveInsertSlotAfter(this.luminary, after_scene_group_id);

        const mediaData = MediaRegistry.getInstance().getItem(media_id);
        if (!mediaData) {
            return `Error: Unknown media ${media_id}`;
        }

        const background = mediaData.background;
        const video = background?.video;
        if (!background || !video) {
            return `Error: media ${media_id} is not a video. Talking-head scene groups require an uploaded video of a person speaking.`;
        }
        if (video.has_audio === false) {
            return `Error: video ${media_id} has no audio. Choose a video clip that contains audio of a person speaking.`;
        }

        const media = ImmutableFromJS(background) as ImmutableMap<SlideBackgroundModel>;

        try {
            const {
                createNewVoicedrivenSceneGroup,
                applyTalkingHeadMedia,
                deleteSceneGroup,
                applyPlaybackRegionsToSceneGroup,
            } = makeOperationsCallable(
                {
                    createNewVoicedrivenSceneGroupOperation,
                    applyTalkingHeadMediaOperation,
                    deleteSceneGroupOperation,
                    applyPlaybackRegionsToSceneGroupOperation,
                },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );

            const { sceneGroupId } = createNewVoicedrivenSceneGroup(
                VideoData.CATEGORY_TALKING_HEAD,
                slot,
                { deleteOtherScenes: false, setPlaceholderAsset: false },
                parentActionGroup,
                false,
            );

            // Applies the video as the talking head, transcribes its speech and syncs
            // captions, then checkpoints the video itself. recommendOverlays is false
            // (the agent adds overlays explicitly via add_voiceover_scene), and trimRequired is
            // false so a long clip does not pop the trim modal mid-run.
            const applied = await applyTalkingHeadMedia({
                sceneGroupId,
                media,
                language_code: undefined,
                forceCaptionGeneration: false,
                enhanceAudio: true,
                recommendOverlays: false,
                trimRequired: false,
                systemTriggered: false,
                parentActionGroup,
            });

            // Tear down the half-built scene group after a failure. Deleting the only
            // remaining content scene group throws `not_enough_scenes`; in that case we
            // leave the (empty) group in place rather than let the cleanup error mask the
            // real failure reason — the video is empty either way. Returns whether the
            // group was actually removed so the caller can tailor its message.
            const cleanupSceneGroup = (): boolean => {
                try {
                    deleteSceneGroup(sceneGroupId, false, parentActionGroup);
                    return true;
                } catch {
                    return false;
                }
            };

            if (!applied) {
                const removed = cleanupSceneGroup();
                return removed
                    ? `Error: the video could not be applied as a talking head. Make sure the clip contains clear spoken audio.`
                    : `Error: the video could not be applied as a talking head, and the empty scene group left behind is the only one in the video so it could not be removed automatically. Make sure the clip contains clear spoken audio, then replace this scene group and try again.`;
            }

            // A talking-head scene group is only usable if its transcript actually landed —
            // captions and overlays sync to it. The transcript fetch can succeed on the
            // backend yet fail to populate the scene group; when that happens we must not
            // leave a broken empty voice-driven group behind, so tear it down and report
            // the failure instead of a false success.
            const createdSceneGroup = this.luminary.getSceneGroupById(sceneGroupId);
            const transcript = createdSceneGroup?.originalTranscript;
            const transcriptWords = transcript?.transcriptData?.words || transcript?.words || [];
            if (transcriptWords.length === 0) {
                const removed = cleanupSceneGroup();
                return removed
                    ? `Error: the transcript for video ${media_id} could not be loaded, so the talking-head scene group would have no narration or captions. The clip may still be transcribing — wait a few seconds and try again, or choose a different clip with clear spoken audio.`
                    : `Error: the transcript for video ${media_id} could not be loaded, so the talking-head scene group has no narration or captions, and it is the only scene group in the video so it could not be removed automatically. The clip may still be transcribing — wait a few seconds, then replace this scene group and try again, or choose a different clip with clear spoken audio.`;
            }

            let keptWordSpan: { startTimeMs: number; endTimeMs: number } | undefined;
            if (words && createdSceneGroup) {
                // Matched against the group's OWN transcript, so the cut lands on
                // the same words the agent read — no timestamps to drift.
                const match = findWordSequenceIndices(transcriptWords, words);
                if (!match) {
                    const removed = cleanupSceneGroup();
                    const quoted = `"${words.join(' ')}"`;
                    return removed
                        ? `Error: ${quoted} does not appear in this recording's transcript, so there is no segment to keep. Nothing was added. Copy the words exactly as they appear in the transcript.`
                        : `Error: ${quoted} does not appear in this recording's transcript. The scene group was added covering the full recording and is the only one in the video, so it could not be removed automatically — replace it or retry with words from the transcript.`;
                }
                keptWordSpan = wordSpanToClipRegion(transcriptWords, match);
                // The same cut the clip_select previews use, so a segment picked
                // there is reproduced exactly.
                applyPlaybackRegionsToSceneGroup(createdSceneGroup, [
                    [keptWordSpan.startTimeMs, keptWordSpan.endTimeMs],
                ]);
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            this.startBaseSceneBuilding(sceneGroupId);

            const shortId =
                computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                    sceneGroupId,
                ) ?? sceneGroupId;
            const placement =
                after_scene_group_id && after_scene_group_id.trim() !== ''
                    ? `right after scene group ${after_scene_group_id}`
                    : 'at the start of the video';
            const trimNote = keptWordSpan
                ? ` covering ${Math.round(keptWordSpan.startTimeMs)}ms\u2013${Math.round(keptWordSpan.endTimeMs)}ms of the recording`
                : '';
            return `Success: Added new talking-head scene group ${shortId} ${placement} from media ${media_id}${trimNote}.${newSceneGroupBaseSceneNote()}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding talking head scene group: ${errorMessage}`;
        }
    }
}
